import { AxiosError } from 'axios';
import { appToast } from 'components/Basic/CustomToast';
import { uploadCustomMap } from 'components/CustomMapSelect/api';
import { useMutation } from 'react-query';
import { errorResponseHandler } from 'store/api/AxiosError';
import imageCompression, { Options } from 'browser-image-compression';
import { useDispatch, useSelector } from 'react-redux';
import { addNewCustomMap, addUploadingMap, setUploadProgress } from 'store/slices/CustomMapSlice';
import { RootState } from 'store';
import { useUpdateCustomMap } from './useUpdateCustomMap';

const INITIAL_VISUAL_FACTOR = 0.4;

type PayloadType= Partial<DesignCustomMap> & {
	file: File;
}
export function useUploadCustomMap(uuid: string) {
	const dispatch = useDispatch();
	const { imgWidth, imgHeight, groundMetersPerPixel } = useSelector((s: RootState) => s.roofData.data);
	const { mutateAsync: updateCustomMap } = useUpdateCustomMap(uuid);
	return useMutation({
		mutationKey: `upload-custom-map-${uuid}`,
		mutationFn: async (payload: PayloadType) => {
			const file = payload.file;
			console.log('originalFile instanceof Blob', file instanceof Blob); // true
			console.log(`originalFile size ${file.size / 1024 / 1024} MB`);

			const options: Options = {
				initialQuality: 0.9,
				useWebWorker: true,
				maxWidthOrHeight: 1000,
				onProgress: (p) => {
					const progress = Math.round(p * 0.5);
					dispatch(setUploadProgress(progress));
				}
			};
			const compressedBlob = await imageCompression(file, options);

			// Preserve original filename
			const compressedFile = new File(
				[compressedBlob],
				file.name,
				{ type: compressedBlob.type }
			);
			console.log('compressedFile instanceof Blob', compressedFile instanceof Blob); // true
			console.log(`compressedFile size ${compressedFile.size / 1024 / 1024} MB`); // smaller than maxSizeMB

			payload.file = compressedFile;
			const files = await uploadCustomMap(
				uuid, 
				{ ...payload, file: compressedFile }, 
				{
					onUploadProgress: (percent) => {
						// upload progress (50-100%)
						const uploadProgress = Math.round(50 + percent * 0.45);

						dispatch(setUploadProgress(uploadProgress));
					}
				}
			);
			// await new Promise((resolve) => setTimeout(resolve, 5000*60));
			return files;
		},
		
		onError: (e: AxiosError)=>{
			const { message } = errorResponseHandler(e);
			appToast.error(message);
			dispatch(setUploadProgress(0));
		},
		onSuccess: (uploadedFile : DesignCustomMap)=>{
			const fitScale = Math.min(
				imgWidth / uploadedFile.pixelWidth,
				imgHeight / uploadedFile.pixelHeight
			);
			const initialScale = fitScale * INITIAL_VISUAL_FACTOR;
			const adjusted: DesignCustomMap = {
				...uploadedFile,
				scaleX: initialScale,
				scaleY: initialScale,
				metersPerPixel: groundMetersPerPixel * initialScale,
			};

			dispatch(addNewCustomMap(adjusted));
			dispatch(addUploadingMap(adjusted.id));
			dispatch(setUploadProgress(100));

			updateCustomMap({
				id: adjusted.id,
				payload: {
					scaleX: adjusted.scaleX,
					scaleY: adjusted.scaleY,
					metersPerPixel: adjusted.metersPerPixel,
				},
			});
		}
	});
}
